import type { AuthService } from '@inform-appshell/fusion-kit-contracts';
import type { AxiosError, AxiosInstance, InternalAxiosRequestConfig } from 'axios';

import { markSessionExpired } from './authState';

type RetryableConfig = InternalAxiosRequestConfig & { _retry?: boolean };

// Guard against stacking interceptors when the same singleton axios instance is
// configured more than once (e.g. standalone bootstrap and module mount paths).
const configuredInstances = new WeakSet<AxiosInstance>();

/**
 * Attach auth to an axios instance: inject the bearer token on every request and,
 * on a 401, refresh the token once and replay the request. Concurrent 401s share
 * a single in-flight refresh so the token is only refreshed once.
 */
export function setupTokenInterceptors(axiosInstance: AxiosInstance, authService: AuthService): void {
  if (configuredInstances.has(axiosInstance)) return;
  configuredInstances.add(axiosInstance);

  let refreshPromise: Promise<boolean> | null = null;

  axiosInstance.interceptors.request.use(
    (config) => {
      const token = authService.token;
      if (token) {
        config.headers.set('Authorization', `Bearer ${token}`);
      }
      return config;
    },
    (error) => Promise.reject(error)
  );

  axiosInstance.interceptors.response.use(
    (response) => response,
    async (error: AxiosError) => {
      const original = error.config as RetryableConfig | undefined;
      const is401 = error.response?.status === 401;

      if (is401 && original && !original._retry) {
        original._retry = true;

        // Dedupe concurrent 401s onto one refresh; clear it once settled.
        refreshPromise ??= authService.forceTokenRefresh(30).finally(() => {
          refreshPromise = null;
        });

        // `forceTokenRefresh` resolves false both when the refresh call failed and when
        // the current token was still valid and so never needed refreshing. Those two
        // cases are indistinguishable from the boolean alone, so it isn't a verdict;
        // authService.token below is what decides.
        try {
          await refreshPromise;
        } catch {
          // Ignored, same reason.
        }

        const freshToken = authService.token;
        if (!freshToken) {
          markSessionExpired();
          return Promise.reject(new Error('Session expired'));
        }
        original.headers.set('Authorization', `Bearer ${freshToken}`);
        return axiosInstance(original);
      }

      return Promise.reject(error);
    }
  );
}
